import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Switch, Tooltip } from '@nextui-org/react';
import { Icon } from '@iconify/react';
import {
  INSPECTION_MEDIA_OPTIONS,
  inspectionMediaOption,
  type InspectionMediaType,
} from './inspectionChecklistMedia';

/** Counter behind the temporary ids of items added in the editor */
let nextNewItemId = 0;

/** One checklist row as the editor shows it */
export interface InspectionChecklistItem {
  /** Stable row id: the record id, or a temporary id for an item added in the editor */
  localId: string;
  title: string;
  mediaType: InspectionMediaType;
  required: boolean;
}

export interface InspectionChecklistEditorProps<T extends InspectionChecklistItem> {
  items: T[];
  onChange: (items: T[]) => void;
  /** Header text; defaults to "Items" */
  title?: ReactNode;
  /** Shown in place of the rows, e.g. a spinner while the items load */
  status?: ReactNode;
  readOnly?: boolean;
  /** Shows "Add Item"; defaults to true */
  allowAdd?: boolean;
  /** Outlines items whose title is still blank */
  showErrors?: boolean;
  emptyLabel?: string;
  /** Classes for the outer box; defaults to `overflow-hidden` */
  className?: string;
}

/**
 * Inspection checklist editor: each item's capture type, title and required switch, with
 * remove and "Add Item" (which first asks for the capture type). Used by the Inspection
 * Templates settings screen and by the `inspectionItems` command form field.
 */
export function InspectionChecklistEditor<T extends InspectionChecklistItem>({
  items,
  onChange,
  title,
  status,
  readOnly = false,
  allowAdd = true,
  showErrors = false,
  emptyLabel,
  className,
}: Readonly<InspectionChecklistEditorProps<T>>) {
  const { t } = useTranslation();
  const [isPickingMedia, setIsPickingMedia] = useState(false);
  const [focusItemId, setFocusItemId] = useState<string | null>(null);
  const focusInputRef = useRef<HTMLInputElement>(null);

  const updateItem = (localId: string, changes: Partial<InspectionChecklistItem>) => {
    onChange(items.map((item) => (item.localId === localId ? { ...item, ...changes } : item)));
  };

  const removeItem = (localId: string) => {
    onChange(items.filter((item) => item.localId !== localId));
  };

  // Puts the cursor in the title of a just-added item
  useEffect(() => {
    // The new row can arrive a render later when the parent stores the items elsewhere
    if (!focusItemId || !focusInputRef.current) return;
    focusInputRef.current.focus();
    setFocusItemId(null);
  }, [focusItemId, items]);

  const addItem = (mediaType: InspectionMediaType) => {
    nextNewItemId += 1;
    // The id only keys the row; it never leaves the browser
    const localId = `new-${Date.now()}-${nextNewItemId}`;
    onChange([...items, { localId, title: '', mediaType, required: true } as T]);
    setFocusItemId(localId);
    setIsPickingMedia(false);
  };

  const renderRows = () => {
    if (status) return status;
    return (
      <>
        {items.length === 0 && !isPickingMedia && (
          <p className="px-4 py-4 text-center text-sm text-default-500">
            {emptyLabel ?? (readOnly ? t('No items') : t('No items yet. Add the first item technicians must capture.'))}
          </p>
        )}
        {items.length > 0 && (
          <div className="flex flex-col divide-y divide-divider">
            {items.map((item) => {
              const media = inspectionMediaOption(item.mediaType);
              const isBlank = showErrors && !item.title.trim();
              return (
                <div key={item.localId} className="flex items-center gap-2 py-1 pl-1.5 pr-1.5">
                  <Tooltip content={t(media.label)}>
                    <Icon icon={media.icon} width={13} className={`shrink-0 ${media.className}`} />
                  </Tooltip>
                  <input
                    value={item.title}
                    ref={item.localId === focusItemId ? focusInputRef : undefined}
                    readOnly={readOnly}
                    maxLength={500}
                    placeholder={t('Enter item name')}
                    aria-label={t('Item name')}
                    aria-invalid={isBlank || undefined}
                    className={`h-8 min-w-0 flex-1 rounded-sm bg-default-100 px-2 text-[13px] font-medium text-foreground outline-none placeholder:font-normal placeholder:text-default-500 focus:ring-1 focus:ring-primary${isBlank ? ' ring-1 ring-danger' : ''}`}
                    onChange={(event) => updateItem(item.localId, { title: event.target.value })}
                  />
                  <Switch
                    size="md"
                    color="secondary"
                    isSelected={item.required}
                    isDisabled={readOnly}
                    aria-label={t('Required')}
                    classNames={{ wrapper: 'mr-0' }}
                    onValueChange={(required) => updateItem(item.localId, { required })}
                  />
                  {!readOnly && (
                    <button
                      type="button"
                      className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-danger-50 text-danger hover:bg-danger-100"
                      aria-label={t('Delete Item')}
                      onClick={() => removeItem(item.localId)}
                    >
                      <Icon icon="solar:trash-bin-trash-linear" width={15} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </>
    );
  };

  const renderAdd = () => {
    if (readOnly || !allowAdd || status) return null;
    if (isPickingMedia) {
      return (
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-t border-divider bg-default-50 px-5 py-3">
          <span className="max-w-[300px] text-[13px] text-default-500">
            {t('What type of media should be captured for this item?')}
          </span>
          <div className="flex flex-wrap items-center gap-2">
            {INSPECTION_MEDIA_OPTIONS.map((option) => (
              <button
                key={option.key}
                type="button"
                className="flex h-8 items-center gap-1.5 rounded-md border border-default-200 bg-content1 px-3 text-xs font-medium text-default-600 hover:border-primary hover:text-primary"
                onClick={() => addItem(option.key)}
              >
                <Icon icon={option.icon} width={13} />
                {t(option.label)}
              </button>
            ))}
          </div>
        </div>
      );
    }
    return (
      <div className="border-t border-divider px-4 py-3">
        <button
          type="button"
          className="flex h-11 w-full items-center justify-center gap-1.5 rounded-lg border-2 border-dashed border-default-200 text-sm font-medium text-default-500 hover:border-primary hover:text-primary"
          onClick={() => setIsPickingMedia(true)}
        >
          <Icon icon="mdi:plus" width={16} />
          {t('Add Item')}
        </button>
      </div>
    );
  };

  return (
    <div className={`rounded-lg border border-divider ${className ?? 'overflow-hidden'}`}>
      <div className="border-b border-divider px-4 py-3 text-sm font-semibold text-foreground">
        {title ?? t('Items')}
      </div>
      {renderRows()}
      {renderAdd()}
    </div>
  );
}
